import { useState } from 'react';
import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../../../../../utils/url-manager';
import { AdminMiniDescriptor, AdminMiniSendReport } from '../admin-mini.types';

/**
 * The three calls an admin Mini makes, written once instead of once per tool.
 *
 * `mystery-shopper.hooks.ts` held three functions that differed from each other only in their URL,
 * and a second tool would have made six. They also each swallowed a failed request into
 * `console.log` and resolved with `undefined`, so a network error looked to the caller exactly like
 * a refusal with no message - which is how a button ends up doing nothing and saying nothing.
 *
 * Every call here resolves with a report the caller can show, whatever happened.
 */

/** What a caller always gets back, even when the request never reached HQ-API. */
function refusal(message: string): AdminMiniSendReport {
    return { status: 'error', message: message };
}

async function post(route: string, payload: unknown): Promise<AdminMiniSendReport> {
    const url: string = UseUrlManagerCleanUrl({ url: hostUrl + route });
    const token: string = read_cookie('auth');

    try {
        const response: Response = await fetch(new Request(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': token,
                'hq': 'hq',
            }),
        }));

        // 403 is the interesting one: it means the role is missing the ability behind this button,
        // and the body is an access-denied page rather than JSON. Saying so beats "unexpected
        // token < in JSON at position 0", which is what parsing it produces.
        if (response.status === 403) {
            return refusal('You do not have permission to do that.');
        }

        const body: AdminMiniSendReport = await response.json();

        return body ?? refusal('HQ-API answered with nothing.');
    } catch (error: unknown) {
        return refusal(error instanceof Error ? error.message : 'That request could not be sent.');
    }
}

/** Save a run and set it going. Spends credits and sends mail. */
export function useAdminMiniActivate(tool: AdminMiniDescriptor): {
    activating: boolean,
    activate: (payload: unknown) => Promise<AdminMiniSendReport>,
} {
    const [activating, setActivating] = useState<boolean>(false);

    async function activate(payload: unknown): Promise<AdminMiniSendReport> {
        setActivating(true);
        try {
            return await post(tool.routes.activate, payload);
        } finally {
            // In a `finally`, because the old hooks cleared their flag inside `then` only - so a
            // rejected request left the button spinning for ever with no way back.
            setActivating(false);
        }
    }

    return { activating, activate };
}

/** Stop a run that is queued or going. */
export function useAdminMiniCancel(tool: AdminMiniDescriptor): {
    cancelling: boolean,
    cancel: (payload: unknown) => Promise<AdminMiniSendReport>,
} {
    const [cancelling, setCancelling] = useState<boolean>(false);

    async function cancel(payload: unknown): Promise<AdminMiniSendReport> {
        setCancelling(true);
        try {
            return await post(tool.routes.cancel, payload);
        } finally {
            setCancelling(false);
        }
    }

    return { cancelling, cancel };
}

/** Send one message by hand, in the run's own voice. */
export function useAdminMiniSendEmail(tool: AdminMiniDescriptor): {
    sending: boolean,
    sendEmail: (payload: unknown) => Promise<AdminMiniSendReport>,
} {
    const [sending, setSending] = useState<boolean>(false);

    async function sendEmail(payload: unknown): Promise<AdminMiniSendReport> {
        setSending(true);
        try {
            return await post(tool.routes.sendEmail, payload);
        } finally {
            setSending(false);
        }
    }

    return { sending, sendEmail };
}
